import { PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { useSecondaryPanelNextNavigation } from 'customHooks/useSecondaryPanelNextNavigation';
import {
  getIsCurrentPanelFilled,
  getIsCurrentPanelRequired,
  getNextRequiredPanel,
  shouldSkipNextRequiredPanelException,
} from 'store/dealManagement/selectors/requiredPanelFlow';
import { changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import SecondaryPanel from 'lib/SecondaryPanel';
import { PlannerSecondaryPanelProps } from './PlannerSecondaryPanel.types';

const PlannerSecondaryPanel = ({
  children,
  customStyles,
  size,
  isLineLevel = true,
  title,
  isDisabled,
  showName,
  showBackButton,
  cancelButtonLabel,
  confirmButtonLabel,
  confirmButtonColor,
  onSubmit,
  onCancel,
  isSubsection,
  isMainLevel,
  isChange,
  isOutsideClickDisabled,
  pendoId,
}: PropsWithChildren<PlannerSecondaryPanelProps>): React.ReactElement => {
  const dispatch = useDispatch();

  const dealLineName = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.name);
  const dealName = useSelector((state: Store) => state.dealManagement.commonDeal.dealName);

  const currentPanel = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);
  const isCurrentPanelRequired = useSelector(getIsCurrentPanelRequired);
  const isCurrentPanelFilled = useSelector(getIsCurrentPanelFilled);
  const nextRequiredPanel = useSelector(getNextRequiredPanel);

  const name = isLineLevel ? dealLineName : dealName;

  const { handleSubmit, submitButtonLabel } = useSecondaryPanelNextNavigation({
    currentPanel,
    isCurrentPanelRequired,
    isCurrentPanelFilled,
    nextRequiredPanel,
    isChange,
    confirmButtonLabel,
    onSubmit,
    onNavigateNext: (nextPanel) => dispatch(changePlannerSecondaryPanelType(nextPanel)),
    shouldSkipNextPanel: shouldSkipNextRequiredPanelException,
  });

  return (
    <SecondaryPanel
      size={size}
      title={title}
      isDisabled={isDisabled}
      showName={showName}
      name={name}
      showBackButton={showBackButton}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={submitButtonLabel}
      confirmButtonColor={confirmButtonColor}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isSubsection={isSubsection}
      isMainLevel={isMainLevel}
      isChange={isChange}
      isOutsideClickDisabled={isOutsideClickDisabled}
      pendoId={pendoId}
      customStyles={customStyles}
    >
      {children}
    </SecondaryPanel>
  );
};

export default PlannerSecondaryPanel;
